import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import CollapsibleSidebar from "./CollapsibleSidebar";

// ─────────────────────────────────────────────────────────────────────────────
// MobileDrawer
// Wrapper que renderiza el sidebar completo dentro de un panel deslizable
// para mobile (<992px). Reutiliza CollapsibleSidebar — NO duplica menú —
// y neutraliza sus estilos fixed/width via la clase .mobile-drawer__panel.
//
// Cierre automático en: cambio de ruta, click backdrop, Esc, click en ítem.
// El "click en ítem" se captura escuchando clicks dentro del aside del sidebar
// sobre cualquier <button class="csb-nav-item">; es la forma menos invasiva
// (no obliga a pasar props a CollapsibleSidebar).
// ─────────────────────────────────────────────────────────────────────────────
export default function MobileDrawer({ open, onClose, fetch }) {
  const { t } = useTranslation();
  const location = useLocation();
  const panelRef = useRef(null);
  const lastPathRef = useRef(location.pathname);

  // Cerrar al cambiar de ruta (cuando el usuario navega desde el drawer).
  useEffect(() => {
    if (open && location.pathname !== lastPathRef.current) {
      onClose();
    }
    lastPathRef.current = location.pathname;
  }, [location.pathname, open, onClose]);

  // Esc cierra.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Bloquear scroll del body mientras está abierto.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Cerrar al hacer click en un ítem de navegación (csb-nav-item).
  // El click event burbujea por el aside; comprobamos el target.
  const handlePanelClick = (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const navBtn = target.closest(".csb-nav-item, .csb-create-btn");
    if (navBtn) {
      // The wizard is rendered by the sidebar. Keep the drawer mounted while
      // it opens so the newly opened modal is not immediately unmounted.
      if (navBtn.classList.contains("csb-create-btn")) return;
      // Cerramos sólo si el ítem no es el toggle de colapso (el sidebar interno
      // ignora ese toggle en mobile; igual blindamos).
      onClose();
    }
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="mobile-drawer" role="dialog" aria-modal="true" aria-label={t("Navegación")}>
      <button
        type="button"
        className="mobile-drawer__backdrop"
        aria-label={t("Cerrar menú")}
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className="mobile-drawer__panel"
        onClick={handlePanelClick}
      >
        <button
          type="button"
          className="mobile-drawer__close"
          onClick={onClose}
          aria-label={t("Cerrar menú")}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            close
          </span>
        </button>
        <CollapsibleSidebar fetch={fetch} forceExpanded />
      </div>
    </div>,
    document.body
  );
}
